import {
  Meeting_Step_Type_Enum,
  useCircleMeetingsSubscription,
  useGetMeetingStepsQuery,
} from '@gql'
import { MeetingStepDataThreads } from '@rolebase/shared/model/meeting_step'
import { MeetingThreadsSortMode } from '@rolebase/shared/model/threadWeather'
import { useContext, useMemo } from 'react'
import { MeetingContext } from '../contexts/MeetingContext'

interface InheritedThreadsSort {
  sortMode?: MeetingThreadsSortMode
  membersOrder?: string[]
}

/*** What the last meeting of this circle had settled (#89, REU89-D8)
 *
 * 🔑 Nothing new is stored: the setting is READ back from the previous meeting.
 * A meeting keeps no link to its template — `stepsConfig` is copied at creation
 * and the template is forgotten — so there is no durable place to write it
 * short of a new table.
 *
 * The inherited value is never written on opening: it only fills the screen
 * until someone touches the sort, and that first write freezes it for this
 * meeting alone.
 *
 * `enabled` is false as soon as the step carries its own setting, which skips
 * both requests: the common case costs nothing.
 */
export default function useInheritedThreadsSort(
  stepType: Meeting_Step_Type_Enum,
  enabled: boolean
): InheritedThreadsSort {
  const { circle, meeting } = useContext(MeetingContext)!

  const { data: meetingsData } = useCircleMeetingsSubscription({
    skip: !enabled || !circle,
    variables: { circleId: circle?.id! },
  })

  // The last one that is over, and not this one: a meeting still running has
  // nothing to hand down yet.
  const previousMeetingId = useMemo(
    () =>
      meetingsData?.meeting.find((m) => m.ended && m.id !== meeting?.id)?.id,
    [meetingsData, meeting]
  )

  const { data: stepsData } = useGetMeetingStepsQuery({
    skip: !enabled || !previousMeetingId,
    variables: { meetingId: previousMeetingId! },
  })

  return useMemo(() => {
    // Its counterpart, type for type: a governance step inherits from a
    // governance step, never from the tactical one (REU89-D17).
    const previousStep = stepsData?.meeting_step.find(
      (s) => s.type === stepType
    )
    const data = previousStep?.data as MeetingStepDataThreads | undefined
    return { sortMode: data?.sortMode, membersOrder: data?.membersOrder }
  }, [stepsData, stepType])
}
